import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { Divider, Menu, MenuItem, Stack } from "@mui/material";
import Cookies from "js-cookie";
import { CustomDataGrid } from "../../../../components/common/CustomDataGrid";
import useTenant from "../../../../components/hooks/useTenant";
import baseURL from "../../../../configs/api";
import { useTheme } from "../../../../components/hooks/useTheme";
import EmailSentDetails from "./EmailSentDetails";
import useConfirm from "../../../../components/hooks/useConfirm";
import moment from "moment";
import useGeneral from "../../../../components/hooks/useGeneral";
import colorConfigs from "../../../../configs/colorConfigs";

const filterItems = [
  {
    id: 0,
    label: "Email",
  },
]

export const AllEmailsSent = () => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { themeStyles } = useTheme();
  const { dateFormat }: any = useGeneral();
  const { handleTenantError }: any = useTenant();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchTableData, setSearchTableData] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [emailSentFilterValue, setEmailSentFilterValue] = useState(0);
  const [particularEmailDetails, setParticularEmailDetails] = useState<any>();
  const [editPopup, setEditPopup] = useState<boolean>(false);
  const [totalLength, setTotalLength] = useState<number>(0);
  const [selectedFilter, setSelectedFilter] = useState<string>(filterItems[0].label);
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
  const columnFilterOpen = Boolean(columnFilterAnchorEl);
  const initialColumns: GridColDef[] = [
    {
      field: "realId",
      headerName: "#",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "recipientEmail",
      headerName: "To Email",
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <span
            style={{ color: "var(--app-link-color)" }}
            className="underline cursor-pointer"
            onClick={() => {
              setEditPopup(true);
              setParticularEmailDetails(params.row);
            }}
          >
            {params?.value}
          </span>
        );
      },
    },
    {
      field: "itemType",
      headerName: "Item Type",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "totalEmails",
      headerName: "Total Emails",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "eventType",
      headerName: "Status",
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const value = (params.value || "").toLowerCase();

        let color = {
          colorBg: "bg-gray-200",
          colorText: "text-gray-600",
        };

        switch (value) {
          case "processed":
          case "queue":
          case "queued":
            color = {
              colorBg: "bg-[#E2E8F0]",
              colorText: "text-[#475569]",
            };
            break;

          case "delivered":
            color = {
              colorBg: "bg-[#D9F4D4]",
              colorText: "text-[#039855]",
            };
            break;

          case "deferred":
            color = {
              colorBg: "bg-[#FFF4CC]",
              colorText: "text-[#B45309]",
            };
            break;

          case "bounce":
          case "bounced":
            color = {
              colorBg: "bg-[#FEDFDD]",
              colorText: "text-[#D92D20]",
            };
            break;

          case "blocked":
          case "dropped":
            color = {
              colorBg: "bg-[#FBEAEA]",
              colorText: "text-[#B91C1C]",
            };
            break;

          case "spamreport":
            color = {
              colorBg: "bg-[#FFE4E6]",
              colorText: "text-[#BE123C]",
            };
            break;

          case "unsubscribe":
            color = {
              colorBg: "bg-[#E9D5FF]",
              colorText: "text-[#6D28D9]",
            };
            break;

          case "open":
          case "opened":
            color = {
              colorBg: "bg-[#FEF9C3]",
              colorText: "text-[#92400E]",
            };
            break;

          default:
            break;
        }

        let displayStatus = "";

        if (value === "processed") {
          displayStatus = "Queued";
        } else if (value === "dropped") {
          displayStatus = "Failed";
        } else {
          displayStatus = params.value
            ? params.value.charAt(0).toUpperCase() + params.value.slice(1).toLowerCase()
            : "";
        }

        return (
          <div className="flex items-end h-full">
            <span
              className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${color.colorBg} ${color.colorText}`}
            >
              {displayStatus}
            </span>
          </div>
        );
      },
    },
    {
      field: "createdBy",
      headerName: "Sent By",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "hasAttachment",
      headerName: "Attachment?",
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const isYes = params.value?.toLowerCase() === "yes";

        return (
          <div className="flex items-end h-full">
            <span
              className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold
            ${isYes ? "bg-[#D9F4D4] text-[#039855]" : "bg-[#FBEAEA] text-[#B91C1C]"}`}
            >
              {params.value}
            </span>
          </div>
        );
      },
    },
    {
      field: "timestamp",
      headerName: "Send At",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "resend",
      headerName: "Resend",
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <button
            className={`text-xs px-3 py-1 rounded-md ${colorConfigs.btn.main.tailwind} border ${themeStyles.main.borderColor} text-white transition`}
            onClick={(e) => {
              e.stopPropagation();
              openResendConfirm(params.row.realId);
            }}
          >
            Resend
          </button>
        );
      },
    }
  ];

  const [columns, setColumns] = useState<GridColDef[]>(initialColumns);
  const [selectedColumns, setSelectedColumns] = useState(initialColumns?.map(col => col?.headerName));

  const openResendConfirm = (id: number) => {
    setConfirmData({
      title: "Resend Email?",
      summary: "Are you sure you want to resend this email?",
      confirmBtn: "Yes",
      isConfirm: (callback: any) => {
        callback();
        handleResend(id);
      }
    });

    setConfirmPopup(true);
  };

  const handleResend = async (id: number, done?: () => void) => {
    try {
      setLoading(true);

      await fetch(`${baseURL}resend/${id}`, {
        method: "POST",
        headers: {
          "X-Tenant-ID": tenantId,
        },
      });

      emptySearchApiCall(paginationModel);
    } catch (err) {
      console.error("Resend error:", err);

    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColumnFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnFilterAnchorEl(event.currentTarget);
  };
  const handleColumnFilterClose = () => {
    setColumnFilterAnchorEl(null);
  };

  const toggleColumn = (headerName: string) => {
    setSelectedColumns(prev =>
      prev.includes(headerName) ? prev.filter(col => col !== headerName) : [...prev, headerName]
    );
  };

  useEffect(() => {
    const filterCol = initialColumns?.filter(col => selectedColumns?.includes(col?.headerName));
    setColumns(filterCol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns])

  const toggleAll = () => {
    if (selectedColumns?.length === initialColumns?.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(initialColumns?.map(col => col?.headerName));
    }
  };

  useEffect(() => {
    let timeOutId: NodeJS.Timeout;

    if (searchValue !== "") {
      timeOutId = setTimeout(() => {
        const newPaginationModel = {
          page: 0,
          pageSize: paginationModel?.pageSize,
        };

        setPaginationModel(newPaginationModel);
      }, 1000);
    }

    return () => clearTimeout(timeOutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (searchValue !== "") {
      apiCall(paginationModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel])

  const apiCall = (newPaginationModel: any) => {
    let filterData: any[] = [];
    setLoading(true);
    switch (emailSentFilterValue) {
      case 0:
        fetch(`${baseURL}emailLogs/GetEmailLogsSearchWithEmailLog?email=${searchValue}&page=${newPaginationModel?.page}&size=${newPaginationModel?.pageSize}`, {
          headers: {
            'X-Tenant-ID': tenantId,
          }
        })
          .then((res) => res.json())
          .then((data) => {
            filterData = data?.emailLogs?.map((item: any, index: number) => {
              return {
                ...item,
                id: index,
                realId: item.id,
                hasAttachment: item.hasAttachment ? "Yes" : "No",
                timestamp: item.timestamp
                  ? moment(new Date(item.timestamp)).format(dateFormat + " hh:mm a")
                  : "",
              };
            })

            setSearchTableData(filterData);
            setTotalLength(data?.count);
            setLoading(false);
            handleTenantError(null);
          })
          .catch((error) => {
            setSearchTableData([]);
            setTotalLength(0);
            setLoading(false);
            handleTenantError(error);
          });
        break;
    }
  }

  useEffect(() => {
    if (searchValue === "" && !isFirstTime) {
      const newPaginationModel = {
        page: 0,
        pageSize: paginationModel?.pageSize,
      };

      setPaginationModel(newPaginationModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (searchValue === "") {
      setIsFirstTime(false)
      emptySearchApiCall(paginationModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel])

  const emptySearchApiCall = (newPaginationModel: any) => {
    setLoading(true);
    fetch(`${baseURL}emailLogs/GetEmailLogsPaging/${newPaginationModel?.page},${newPaginationModel?.pageSize}`, {
      headers: {
        'X-Tenant-ID': tenantId,
      }
    })
      .then((res) => res.json())
      .then((data) => {
        const result = data?.emailLogs?.map((item: any, index: number) => {
          return {
            ...item,
            id: index,
            realId: item.id,
            hasAttachment: item.hasAttachment ? "Yes" : "No",
            timestamp: item.timestamp
              ? moment(new Date(item.timestamp)).format(dateFormat + " hh:mm a")
              : "",
          };
        })

        setSearchTableData(result);
        setTotalLength(data?.count);
        setLoading(false);
        handleTenantError(null);
      })
      .catch((error) => {
        setSearchTableData([]);
        setTotalLength(0);
        setLoading(false);
        handleTenantError(error);
      });
  }

  return (
    <div className="w-full mt-2">
      <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
        <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>
          <div className="flex items-center gap-1.5">

            <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Email Sent</h2>

            <div >
              <button
                className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                aria-controls={columnFilterOpen ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={columnFilterOpen ? 'true' : undefined}
                onClick={handleColumnFilterClick}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>Columns</span>
                  <span className={`material-symbols-outlined text-[18px] font-light ${themeStyles.main.textHeadingColor}`}>
                    filter_alt
                  </span>
                </Stack>
              </button>

              <Menu
                id="basic-menu"
                anchorEl={columnFilterAnchorEl}
                open={columnFilterOpen}
                onClose={handleColumnFilterClose}
                slotProps={{
                  paper: {
                    sx: {
                      width: 170,
                      maxHeight: 300,
                      backgroundColor: themeStyles.topBar.bg
                    }
                  }
                }}
              >
                <div className="flex items-center gap-2 px-[16px] py-[6px] w-full">
                  <input
                    type="checkbox"
                    className="form-checkbox cursor-pointer"
                    checked={selectedColumns?.length === initialColumns?.length ? true : false}
                    onClick={toggleAll}
                  />
                  <span className={`${themeStyles.topBar.tailwindTextColor} font-medium text-[12px]`}>Select All</span>
                </div>
                <Divider className="w-full" />
                {
                  initialColumns?.map((column, index) => (
                    <>
                      <MenuItem
                        key={index}
                        sx={{
                          fontSize: "12px",
                          marginBlock: "0px",
                          "&: hover": {
                            cursor: "default"
                          }
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="form-checkbox cursor-pointer"
                            checked={selectedColumns.includes(column?.headerName)}
                            onChange={() => toggleColumn(column?.headerName || "")}
                          />
                          <span className={`${themeStyles.topBar.tailwindTextColor} font-medium`}>{column?.headerName}</span>
                        </div>

                      </MenuItem>
                      <Divider className="w-full" />
                    </>

                  ))
                }
              </Menu>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <div >
              <button
                className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>{selectedFilter}</span>
                  {open ? <span className={`material-symbols-outlined ${themeStyles.topBar.tailwindTextColor} text-[25px] font-extralight`}>
                    keyboard_arrow_up
                  </span> : <span className={`material-symbols-outlined ${themeStyles.topBar.tailwindTextColor} text-[25px] font-extralight`}>
                    keyboard_arrow_down
                  </span>}
                </Stack>
              </button>

              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  paper: {
                    sx: {
                      width: 200,
                      backgroundColor: themeStyles.topBar.bg
                    }
                  }
                }}
              >
                {
                  filterItems?.map((filter, index) => (
                    <>
                      <MenuItem
                        key={index}
                        sx={{ fontSize: "12px", marginBlock: "0px" }}
                        onClick={() => {
                          setSelectedFilter(filter.label)
                          setEmailSentFilterValue(filter.id)
                          setAnchorEl(null);

                        }}>
                        <span className={`${themeStyles.topBar.tailwindTextColor} text-[16px] font-medium`}>{filter.label}</span>
                      </MenuItem>
                      <Divider className="w-full" />
                    </>

                  ))
                }
              </Menu>
            </div>
            <div className={`flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border ${themeStyles.main.borderColor} shadow-sm`}>
              <span className={`material-symbols-outlined text-[25px] font-light ${themeStyles.main.textSubHeadingColor}`}>
                search
              </span>
              <input
                type="text"
                placeholder="Search..."
                value={searchValue}
                className={`border-none outline-none w-full ${themeStyles.main.textSubHeadingColor} bg-transparent`}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
          </div>
        </div>

        <CustomDataGrid
          filteredRows={searchTableData}
          columns={columns?.map(col => ({
            ...col,
            headerClassName: themeStyles.table.headerBg,
          }))}
          loading={loading}
          totalLength={totalLength}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
        />
      </div>
      <EmailSentDetails
        particularEmailDetails={particularEmailDetails}
        editPopup={editPopup}
        hidePopup={() => setEditPopup(false)}
      />
    </div>
  )
}
